import { useState, useCallback } from "react";
import type { ReactNode } from "react";

export interface FilterField {
  key: string;
  label: string;
  type: "select";
  options: { value: string; label: string }[];
}

export interface SortOption {
  value: string;
  label: string;
}

interface FilterBarProps {
  search?: string;
  onSearchChange?: (val: string) => void;
  searchPlaceholder?: string;
  sortValue?: string;
  onSortChange?: (val: string) => void;
  sortOptions?: SortOption[];
  timeRange?: string;
  onTimeRangeChange?: (val: string) => void;
  filters?: FilterField[];
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, val: string) => void;
  timePresetOptions?: { label: string; value: string; days: number }[];
  customDateRange?: { start: string; end: string };
  onCustomDateChange?: (start: string, end: string) => void;
  showCustomDate?: boolean;
  onReset?: () => void;
  activeCount?: number;
  children?: ReactNode;
}

const defaultTimePresets = [
  { label: "最近 7 天", value: "7d", days: 7 },
  { label: "最近 30 天", value: "30d", days: 30 },
  { label: "最近 90 天", value: "90d", days: 90 },
  { label: "最近 1 年", value: "365d", days: 365 },
];

export function getTimeFilterISO(range: string): string | null {
  if (!range || range === "custom") return null;
  const preset = defaultTimePresets.find((p) => p.value === range);
  if (!preset) return null;
  const start = new Date();
  start.setDate(start.getDate() - preset.days);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

export default function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "搜索...",
  timeRange,
  onTimeRangeChange,
  filters = [],
  filterValues = {},
  onFilterChange,
  timePresetOptions,
  customDateRange,
  onCustomDateChange,
  showCustomDate,
  onReset,
  activeCount,
  children,
}: FilterBarProps) {
  const presets = timePresetOptions || defaultTimePresets;
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleTimeChange = useCallback(
    (val: string) => {
      if (!onTimeRangeChange) return;
      if (val === "custom") {
        setShowDatePicker(true);
      } else {
        setShowDatePicker(false);
        onTimeRangeChange(val);
      }
    },
    [onTimeRangeChange]
  );

  const count =
    activeCount ??
    [
      search,
      timeRange,
      ...filters.map((f) => filterValues?.[f.key] || ""),
    ].filter(Boolean).length;

  const hasFilters =
    (search !== undefined && onSearchChange) ||
    onTimeRangeChange ||
    filters.length > 0 ||
    children;
  if (!hasFilters) return null;

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-3 bg-white rounded-xl border border-surface-border p-4">
      {/* Search */}
      {onSearchChange && (
        <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={search || ""}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-surface text-sm text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none transition-colors"
          />
        </div>
      )}

      {/* Time Range */}
      {onTimeRangeChange && (
        <div className="relative">
          <select
            value={timeRange || ""}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer"
          >
            <option value="">全部时间</option>
            {presets.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
            {showCustomDate !== false && <option value="custom">自定义日期</option>}
          </select>
          {showDatePicker && onCustomDateChange && (
            <div className="absolute top-full mt-2 left-0 bg-white border border-surface-border rounded-lg shadow-sm p-3 z-20 flex items-center gap-2 min-w-[300px]">
              <input
                type="date"
                value={customDateRange?.start || ""}
                onChange={(e) =>
                  onCustomDateChange(e.target.value, customDateRange?.end || "")
                }
                className="h-9 px-2 rounded-md bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
              />
              <span className="text-text-muted text-sm">至</span>
              <input
                type="date"
                value={customDateRange?.end || ""}
                onChange={(e) =>
                  onCustomDateChange(customDateRange?.start || "", e.target.value)
                }
                className="h-9 px-2 rounded-md bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
              />
              <button
                onClick={() => {
                  if (customDateRange?.start && customDateRange?.end) {
                    onTimeRangeChange?.("custom");
                    setShowDatePicker(false);
                  }
                }}
                className="h-9 px-3 rounded-md bg-brand text-white text-xs font-medium hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                确认
              </button>
            </div>
          )}
        </div>
      )}

      {/* Dynamic Filters */}
      {filters.map((f) => (
        <select
          key={f.key}
          value={filterValues[f.key] || ""}
          onChange={(e) => onFilterChange?.(f.key, e.target.value)}
          className="h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer"
        >
          <option value="">{f.label}</option>
          {f.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ))}

      {/* Extra children */}
      {children}

      {/* Reset */}
      {count > 0 && onReset && (
        <button
          onClick={onReset}
          className="h-10 px-3 rounded-lg text-sm text-text-muted hover:text-brand hover:bg-brand/5 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1" />
          重置 ({count})
        </button>
      )}
    </div>
  );
}